import { useDispatch } from 'react-redux';
import { AryaEvent, AryaFurniture, EnvironmentMapOptions } from '@digital-bridge/artisan-arya';
import styled from '@mui/system/styled';
import Preact from 'preact';
import { forwardRef } from 'preact/compat';
import * as Hooks from 'preact/hooks';
import * as AryaInterface from './arya/interface';
import { useArya } from './hooks/useArya';
import * as O from 'fp-ts/Option';
import { useSelector } from './hooks/redux';
import * as Selectors from '../store/selectors';
import * as SceneActions from '../store/actions/scene';
import { pipe } from 'fp-ts/function';

export type Interface = AryaInterface.Interface;
export type Props = {
    showDebugTools: boolean;
    environmentMap: EnvironmentMapOptions | undefined;
};

const StyledCanvas = styled('canvas')(() => ({
    '&.cursor-default': {
        cursor: 'default',
    },
    '&.cursor-pointer': {
        cursor: 'url(assets/cursors/pointer.svg) 10 10, url(assets/cursors/pointer.png) 10 10, pointer',
    },
    '&.cursor-grab': {
        cursor: 'url(assets/cursors/grab.svg) 10 10, url(assets/cursors/grab.png) 10 10, grab',
    },
    '&.cursor-grabbing': {
        cursor: 'url(assets/cursors/grabbing.svg) 10 10, url(assets/cursors/grabbing.png) 10 10, grabbing',
    },
    '&.cursor-move': {
        cursor: 'move',
    },
    '&.cursor-rotate': {
        cursor: 'url(assets/cursors/rotate.svg) 10 10, url(assets/cursors/rotate.png) 10 10, ew-resize',
    },
}));

function _Component(props: Props, ref: Preact.Ref<O.Option<AryaInterface.Interface>>) {
    const canvas3 = Hooks.useRef<any>();
    const canvas2 = Hooks.useRef<any>();

    const arya = useArya(canvas3, canvas2, props.showDebugTools, props.environmentMap);
    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        const roomDimensionChangedHandler = () => {
            O.toNullable(arya)?.setFloorCovering('paving-new');
        };

        roomDimensionChangedHandler();

        const getProductIds = (furniture: AryaFurniture) =>
            pipe(
                O.Do,
                O.bind('arya', () => arya),
                O.bind('persistentId', ({ arya }) => O.fromNullable(furniture.getPersistentID())),
                O.map(({ arya, persistentId }) =>
                    O.toNullable(arya.furnitureProducts(persistentId)),
                ),
                O.fold(
                    () => [] as string[],
                    productIds => productIds ?? [],
                ),
            );

        const furnitureMoveHandler = (furniture: unknown) => {
            const productIds = getProductIds(furniture as AryaFurniture);
            dispatch(
                SceneActions.furnitureInteractionCompleteAction({
                    productIds,
                    interactionType: 'move',
                }),
            );
        };

        const furnitureRotateHandler = (furniture: unknown) => {
            const productIds = getProductIds(furniture as AryaFurniture);
            dispatch(
                SceneActions.furnitureInteractionCompleteAction({
                    productIds,
                    interactionType: 'rotate',
                }),
            );
        };

        O.toNullable(arya)?.on(AryaEvent.ROOM_DIMENSIONS_CHANGED, roomDimensionChangedHandler);
        O.toNullable(arya)?.on(AryaEvent.FURNITURE_MOVE_COMPLETE, furnitureMoveHandler);
        O.toNullable(arya)?.on(AryaEvent.FURNITURE_ROTATE_COMPLETE, furnitureRotateHandler);

        return () => {
            O.toNullable(arya)?.off(AryaEvent.ROOM_DIMENSIONS_CHANGED, roomDimensionChangedHandler);
            O.toNullable(arya)?.off(AryaEvent.FURNITURE_MOVE_COMPLETE, furnitureMoveHandler);
            O.toNullable(arya)?.off(AryaEvent.FURNITURE_ROTATE_COMPLETE, furnitureRotateHandler);
        };
    }, [arya]);

    // this allows the outside world to access the AryaInterface directly
    Hooks.useImperativeHandle(ref, () => arya, [arya]);

    return (
        <>
            <StyledCanvas
                ref={canvas3}
                style={{
                    width: '100%',
                    height: '100%',
                }}
            />
            <canvas
                ref={canvas2}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'none',
                }}
            />
        </>
    );
}

export const Component = forwardRef<O.Option<AryaInterface.Interface>, Props>(_Component);
